import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useLetter from 'app/hooks/useLetter';
import {
    Paper,
    Grid,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Skeleton,
    TableContainer,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import StatusLabel from 'app/components/Form/Letter/Label/StatusLabel';

dayjs.extend(utc);
dayjs.extend(timezone);

const InitialColor = (status) => {
    switch (status) {
        case "SUBMITTED":
            return "info.main";
        case "APPROVED":
            return "success.main";
        case "REJECTED":
            return "error.main";
        default:
            return "warning.main";
    }
};

const LabelValue = ({ label, value }) => {
    const isStatus = label === "STATUS";

    return (
        <>
            <Typography fontSize={12} fontWeight="bold">
                {label}
            </Typography>

            {isStatus ? (
                <Typography
                    fontSize={14}
                    fontWeight="bold"
                    sx={{
                        color: InitialColor(value),
                        textTransform: "uppercase"
                    }}
                >
                    {value || "-"}
                </Typography>
            ) : (
                <Typography fontSize={14}>
                    {(value || "-").toUpperCase()}
                </Typography>
            )}
        </>
    );
};

export default function QRLetterViewer() {
    const { GetQRViewLetter, letter } = useLetter();
    const { CLIENT_KEY } = useParams();

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (CLIENT_KEY) {
            setIsLoading(true);
            GetQRViewLetter(CLIENT_KEY).finally(() => {
                setIsLoading(false);
            });
        }
    }, [CLIENT_KEY]);

    const labelItems = [
        "LETTER NUMBER",
        "LETTER COMPANY",
        "INTERNAL MEMO DESCRIPTION",
        "CREATED BY",
        "CREATED DATE",
        "STATUS"
    ];

    const fields = letter && [
        letter.cm_code,
        `${letter.cm_company_code} - ${letter.cm_company_name}`,
        `${letter.cm_im_number} - ${letter.cm_im_title}`,
        letter.cm_created_by,
        dayjs(letter.cm_created_at).utcOffset(7).format("DD MMMM YYYY, HH:mm"),
        Number(letter.cm_status) === 1
            ? "SUBMITTED"
            : Number(letter.cm_status) === 2
                ? "WAITING FOR APPROVAL"
            : Number(letter.cm_status) === 3
                ? "APPROVED"
                : Number(letter.cm_status) === 999
                    ? "REJECTED"
                    : "DRAFTED"
    ];

    return (
        <Paper
            elevation={3}
            sx={{
                p: 4,
                position: "relative",
                overflow: "hidden",
                bgcolor: "#fff",
                borderRadius: '0px'
            }}
        >
            {/* HEADER BOX */}
            <Box
                sx={{
                    p: 2,
                    border: "1px solid #ddd",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px"
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Typography fontSize={20} fontWeight="bold">
                            DETAIL OF COMMUNICATION LETTER
                        </Typography>
                    </Grid>
                    {labelItems.map((label, i) => (
                        <Grid item key={i} xs={12} md={6}>
                            {isLoading ? (
                                <>
                                    <Skeleton width="40%" height={18} />
                                    <Skeleton width="80%" height={26} />
                                </>
                            ) : (
                                <LabelValue label={label} value={fields[i]} />
                            )}
                        </Grid>
                    ))}

                </Grid>
            </Box>

            {isLoading ? (
                <Box sx={{ p: 2 }}>
                    <Skeleton height={24} width="60%" />
                    <Skeleton height={24} />
                    <Skeleton height={24} />
                    <Skeleton height={24} width="80%" />
                </Box>
            ) : (
                <Box
                    sx={{
                        mt: 1,
                        p: 2,
                        border: "1px solid #ddd",
                        backgroundColor: "#fafafa",
                        borderRadius: "8px"
                    }} 
                    dangerouslySetInnerHTML={{ __html: letter.cm_description }} 
                />
            )}

            <Box
                sx={{
                    mt: 1,
                    p: 2,
                    border: "1px solid #ddd",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px"
                }}
            >
                <Typography fontSize={12} fontWeight="bold" mb={1}>
                    ATTACHMENT :
                </Typography>

                {isLoading ? (
                    <Skeleton width="40%" />
                ) : (
                    <Typography>{letter?.cm_document || "-"}</Typography>
                )}
            </Box>
            
            <Box
                sx={{
                    mt: 1,
                    p: 2,
                    border: "1px solid #ddd",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px"
                }}
            >
                <Typography variant="subtitle1" align="center" fontWeight="bold" mb={1}>
                    APPROVERS INFORMATION
                </Typography>

                <TableContainer component={Paper} elevation={1}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className='bg-dark text-white' sx={{ width: 60 }} align="center">NO</TableCell>
                                <TableCell className='bg-dark text-white' sx={{ width: '20%'}}>APPROVED BY</TableCell>
                                <TableCell className='bg-dark text-white' sx={{ width: '37%'}} align="left">REASON OF APPROVAL</TableCell>
                                <TableCell className='bg-dark text-white' sx={{ width: '20%'}} align="left">DATE OF APPROVAL</TableCell>
                                <TableCell className='bg-dark text-white' sx={{ width: '15%'}}align="center">STATUS</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isLoading ? (
                                [...Array(2)].map((_, i) => (
                                    <TableRow key={i}>
                                        {[...Array(5)].map((_, j) => (
                                            <TableCell key={j}>
                                                <Skeleton width="80%" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : letter.approvers && letter.approvers.length > 0 ? (
                                letter.approvers.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell align="center">{index + 1}</TableCell>
                                        <TableCell>
                                            <strong variant='subtitle'>{row.cm_employee_name?.toUpperCase()}</strong><br/>
                                            <small className="text-muted">{row.cm_position?.toUpperCase()}</small>
                                        </TableCell>
                                        <TableCell align="left">{row.cm_note || "-"}</TableCell>
                                        <TableCell align="left">
                                            <small>{!row.cm_date
                                                ? "-"
                                                : dayjs(row.cm_date).utcOffset(7).format("DD MMMM YYYY, HH:mm")}</small>
                                        </TableCell>
                                        <TableCell align="center">
                                            <StatusLabel status={row.cm_status} />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography fontWeight="bold" sx={{ color: "error.main" }}>
                                            -- NO APPROVER AVAILABLE --
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Paper>
    );
}
